import * as routerUtils from 'Utils/router';
import analytics from 'Analytics';
import { httpClientUser } from 'Api/microservice-http-clients';
import auth from 'Auth';
import CONFIG from 'Config';
import { SERVER_AUTH_ERROR_MESSAGE } from 'Config/constans';
import { SET_ALL } from './mutation-types';
import initialState from './state';

export default {
  async initAuth({ dispatch, getters }) {
    // Set login data update handler.
    // It can be by update storage from another tab
    if (!auth.onLoginDataUpdate) {
      auth.onLoginDataUpdate = loginData => dispatch('loginDataUpdateHandler', loginData);
    }
    // If user is not logged in, try to initialize auth
    if (!getters.isLoggedIn) {
      try {
        // waiting for auth initialization
        const initAuthResult = await auth.init();
        // if auth result is not null, means there is login data
        if (initAuthResult?.loginData) {
          // so save it to store
          dispatch('setLoginData', initAuthResult.loginData);
        }
        return initAuthResult;
      } catch (error) {
        if (error.message === 'auth_timeout') {
          // eslint-disable-next-line no-console
          console.log('==debug== error auth timeout');
          // Logout and show toaster with error
          dispatch('logout', true);
          dispatch('toaster/setMessage', {
            text: SERVER_AUTH_ERROR_MESSAGE,
            options: {
              type: 'error',
              timeout: 10000,
            },
          }, { root: true });
        }
      }
    } else {
      // Otherwise, user is logged in, so check token
      try {
        dispatch('checkToken');
      } catch (error) { /* ignore errors on check token, because all sets inside checkToken method */ }
    }
    return null;
  },
  loginDataUpdateHandler({ dispatch }, loginData) {
    if (!loginData || loginData.token === null) {
      dispatch('logout', true);
    } else {
      dispatch('setLoginData', loginData);
    }
  },
  checkToken({ dispatch, getters }) {
    if (!getters.isLoggedIn) return Promise.resolve();
    const tokenTimeLeft = Date.parse(auth.expirationTime) - Date.now();
    // If token already expired, logout immediately
    if (tokenTimeLeft < 0) {
      dispatch('tokenExpiredHandler');
    // Refresh token if it on refresh token period old
    } else if (tokenTimeLeft < (CONFIG.tokenLifetime - CONFIG.refreshTokenPeriod)) {
      return dispatch('refreshToken');
    }
    return Promise.resolve();
  },
  async refreshToken({ dispatch, state, commit }) {
    if (state.tokenRefreshLoading) return;
    try {
      commit(SET_ALL, { tokenRefreshLoading: true });
      const tokenData = await auth.refreshToken();
      dispatch('setLoginData', tokenData);
      commit(SET_ALL, { tokenRefreshLoading: false });
    } catch (e) {
      commit(SET_ALL, { tokenRefreshLoading: false });
      dispatch('logout', true);
    }
  },
  setLoginData({ commit, dispatch }, loginData) {
    const isFirstLogin = loginData.isFirstLogin || false;
    commit(SET_ALL, { ...loginData, tokenExpired: false, isFirstLogin });
    dispatch('setTokenRefreshRequiredTimeout');
    dispatch('setTokenExpireTimeout');
  },
  setTokenRefreshRequiredTimeout({ getters, state, commit }) {
    commit(SET_ALL, { tokenRefreshRequired: false });
    // If logged in
    if (getters.isLoggedIn) {
      // Clear previous timeout
      clearTimeout(state.tokenRefreshRequiredTimeout);
      // Create new timeout
      const tokenRefreshRequiredTimeout = setTimeout(() => {
        if (!getters.isLoggedIn) return;
        commit(SET_ALL, {
          tokenRefreshRequired: true,
        });
      }, CONFIG.refreshTokenPeriod);
      // Set new timeout
      commit(SET_ALL, { tokenRefreshRequiredTimeout });
    }
  },
  setTokenExpireTimeout({ state, dispatch, commit }) {
    // If expiration time set
    if (state.expirationTime) {
      // Clear previous timeout
      clearTimeout(state.tokenExpireTimeout);
      // Get token time left
      const tokenTimeLeft = Date.parse(state.expirationTime) - Date.now();
      // Create new timeout
      const tokenExpireTimeout = setTimeout(() => {
        dispatch('tokenExpiredHandler');
      }, tokenTimeLeft);
      // Set new timeout
      commit(SET_ALL, { tokenExpireTimeout });
    }
  },
  async tokenExpiredHandler({ dispatch, commit, getters }) {
    // Do nothing if user is logged in
    if (!getters.isLoggedIn) return;
    // Dispatch logout
    await dispatch('logout', true);
    setTimeout(() => {
      // Set token expired
      commit(SET_ALL, { tokenExpired: true });
    });
  },
  async getTemporaryToken({ commit }) {
    const { data } = await httpClientUser.get('/temporary_token');
    commit(SET_ALL, {
      temporaryToken: data.token,
    });
    return data.token;
  },
  async logoutActions({ dispatch, commit, state }) {
    commit(SET_ALL, { logoutLoading: true });
    await auth.logout();
    clearTimeout(state.tokenExpireTimeout);
    commit(SET_ALL, initialState());
    await dispatch('router/checkRouteAfterLogout', null, { root: true });
    dispatch('farm/resetFarmOnLogout', null, { root: true });
    dispatch('profile/resetProfile', false, { root: true });
    dispatch('analytics/resetAnalytics', false, { root: true });
    dispatch('notifications/resetNotifications', false, { root: true });
    dispatch('investment/resetInvestmentList', false, { root: true });
    dispatch('account/resetAccountStore', false, { root: true });
    dispatch('view/resetDocumentsBannerClosed', false, { root: true });
    dispatch('myAdvisors/resetAdvisorsList', false, { root: true });
    commit(SET_ALL, { logoutLoading: false });
  },
  logout({ dispatch, commit, rootGetters }, force) {
    // Get route to find out is route requires confirm on logout
    const router = rootGetters['router/router'];
    const isRequiresConfirm = routerUtils.hasInMeta(router.currentRoute, 'requiresConfirmLogout');
    analytics.triggerUserLogout();
    // Setup promise
    const promise = new Promise((resolve) => {
      // If confirm is not requires or force logout, resolve logout
      if (!isRequiresConfirm || force === true) {
        resolve();
      } else {
        commit(SET_ALL, { logoutResolver: resolve });
      }
    });
    return new Promise(((resolve) => {
      promise
        .then(() => dispatch('logoutActions'))
        .then(resolve);
    }));
  },
  setMFALoading({ commit }, mfaLoading) {
    commit(SET_ALL, { mfaLoading });
  },
  async setMFA({ commit }, mfa = true) {
    commit(SET_ALL, { mfaLoading: true });
    try {
      if (mfa) {
        await auth.enableMFA(mfa === 'reload');
      } else {
        await auth.disableMFA();
      }
      commit(SET_ALL, {
        mfa,
        mfaLoading: false,
      });
    } catch (error) {
      commit(SET_ALL, { mfaLoading: false });
      throw error;
    }
  },
  setIsComingSoonSignUp({ commit }, isComingSoonSignUp = true) {
    commit(SET_ALL, { isComingSoonSignUp });
  },
};
